import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { env } from '@/config/env'
import { isDataMutation, notifyMutation } from '@/lib/freshness'
import { toApiError } from './errors'
import { isUnsupportedSort, reportUnsupportedSort, withoutSort } from './sortSupport'

/**
 * The single API client. Authentication is plugged in by the AuthStore through
 * `bindAuth`, which keeps this module free of store imports (no cycles) and
 * keeps tokens out of anything that gets logged.
 */
type AuthBinding = {
  getAccessToken: () => string | null
  /** Rotates the session; resolves with the new access token or null when rotation failed. */
  refresh: () => Promise<string | null>
  onSessionExpired: () => void
}

let auth: AuthBinding | null = null

export function bindAuth(binding: AuthBinding) {
  auth = binding
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean; _skipAuth?: boolean; _sortDropped?: boolean }

export const http = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeoutMs,
  headers: { Accept: 'application/json' },
})

http.interceptors.request.use((config: RetriableConfig) => {
  const token = config._skipAuth ? null : auth?.getAccessToken()
  if (token) config.headers.set('Authorization', `Bearer ${token}`)
  return config
})

http.interceptors.response.use(
  (response) => {
    // A saved change: lists and dropdowns here and in the other open tabs refresh in the background.
    if (isDataMutation(response.config.method, response.config.url)) notifyMutation()
    return response
  },
  async (error) => {
    const config = error?.config as RetriableConfig | undefined
    const status = error?.response?.status

    // A file request (responseType 'blob': exports) gets its JSON error body as
    // a Blob too. Read it, so the server's reason reaches toApiError instead of
    // the bare «Запрос не выполнен».
    const failedBody = error?.response?.data
    if (failedBody instanceof Blob && failedBody.size < 1_000_000) {
      try {
        error.response.data = JSON.parse(await failedBody.text())
      } catch {
        /* not JSON: the status alone speaks */
      }
    }

    // /me/context is the first database-backed validation of the complete
    // identity carried by the token. The API returns 404 when that context
    // no longer matches an active profile/session, so discard it like expiry.
    if (status === 404 && config?.url?.replace(/^.*\/v1/, '').replace(/^\//, '') === 'me/context' && auth) {
      auth.onSessionExpired()
    }

    // One refresh + one retry per request. The refresh itself is single-flight
    // inside the AuthStore, so concurrent 401s never replay the same token.
    if (status === 401 && config && !config._retried && !config._skipAuth && auth) {
      config._retried = true
      const token = await auth.refresh()
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`)
        return http.request(config)
      }
      auth.onSessionExpired()
    }

    // A column the backend cannot sort by yet: the same list in its default order (see sortSupport.ts).
    if (config && !config._sortDropped && isUnsupportedSort(status, error?.response?.data?.error?.message, config.params)) {
      reportUnsupportedSort(config.url ?? '', config.params.sort)
      return http.request({ ...config, _sortDropped: true, params: withoutSort(config.params) } as RetriableConfig)
    }
    // The same for a `sort` sent in a JSON body (report exports).
    const sentBody = config && typeof config.data === 'string' ? parseJSON(config.data) : undefined
    if (config && !config._sortDropped && isUnsupportedSort(status, error?.response?.data?.error?.message, sentBody)) {
      reportUnsupportedSort(config.url ?? '', (sentBody as { sort?: unknown }).sort)
      return http.request({ ...config, _sortDropped: true, data: JSON.stringify(withoutSort(sentBody)) } as RetriableConfig)
    }

    return Promise.reject(toApiError(error))
  },
)

function parseJSON(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

/** Options for endpoints that must never carry or refresh the bearer token. */
export const publicRequest: AxiosRequestConfig & { _skipAuth: true } = { _skipAuth: true }
